import { useState, useCallback, useEffect, type FC } from 'react';
import { Target, Shield, Crosshair, UserSearch } from 'lucide-react';
import { GuidedTour } from '@/components/onboarding/GuidedTour';
import { usePageTour } from '@/hooks/usePageTour';
import { salesIntelligenceTourSteps } from '@/lib/tour-data';
import StatCard from '@/components/common/StatCard';
import BattleCardGenerator from './components/BattleCardGenerator';
import ColdOutreachGenerator from './components/ColdOutreachGenerator';
import ProspectProfiler from './components/ProspectProfiler';

type TabKey = 'battle-card' | 'outreach' | 'profiler';

const TABS: { key: TabKey; label: string; icon: FC<{ className?: string }> }[] = [
  { key: 'battle-card', label: 'Battle Card', icon: Shield },
  { key: 'outreach', label: 'Cold Outreach', icon: Crosshair },
  { key: 'profiler', label: 'Prospect Profiler', icon: UserSearch },
];

interface Stats {
  battleCards: number;
  outreach: number;
  prospects: number;
}

const TOUR_ID = 'admin-sales-intelligence';

const SalesIntelligencePage: FC = () => {
  const tour = usePageTour(TOUR_ID);
  useEffect(() => {
    const handler = () => tour.startTour();
    window.addEventListener('restart-page-tour', handler);
    return () => window.removeEventListener('restart-page-tour', handler);
  }, [tour]);

  const [activeTab, setActiveTab] = useState<TabKey>('battle-card');
  const [stats, setStats] = useState<Stats>({ battleCards: 0, outreach: 0, prospects: 0 });

  const handleBattleCardGenerated = useCallback(() => {
    setStats((prev) => ({ ...prev, battleCards: prev.battleCards + 1 }));
  }, []);

  const handleOutreachGenerated = useCallback(() => {
    setStats((prev) => ({ ...prev, outreach: prev.outreach + 1 }));
  }, []);

  const handleProspectGenerated = useCallback(() => {
    setStats((prev) => ({ ...prev, prospects: prev.prospects + 1 }));
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3" data-tour="sales-intelligence-header">
          <Target className="w-6 h-6" />
          <div>
            <h1 className="text-xl font-bold">Sales Intelligence</h1>
            <p className="mt-0.5 text-xs italic text-blue-200">Intelligence commerciale</p>
            <p className="mt-0.5 text-sm text-blue-100">
              Preparez vos rendez-vous et prospectez avec l'IA
            </p>
          </div>
        </div>
      </div>

      {/* StatCards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          title="Battle Cards"
          value={stats.battleCards}
          icon={Shield}
          iconColor="text-blue-600 dark:text-blue-400"
          iconBg="bg-blue-50 dark:bg-blue-900/30"
        />
        <StatCard
          title="Outreach envoyés"
          value={stats.outreach}
          icon={Crosshair}
          iconColor="text-amber-600 dark:text-amber-400"
          iconBg="bg-amber-50 dark:bg-amber-900/30"
        />
        <StatCard
          title="Prospects analysés"
          value={stats.prospects}
          icon={UserSearch}
          iconColor="text-emerald-600 dark:text-emerald-400"
          iconBg="bg-emerald-50 dark:bg-emerald-900/30"
        />
      </div>

      {/* Tabs */}
      <div className="fc-card overflow-hidden">
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'battle-card' && <BattleCardGenerator onGenerated={handleBattleCardGenerated} />}
          {activeTab === 'outreach' && <ColdOutreachGenerator onGenerated={handleOutreachGenerated} />}
          {activeTab === 'profiler' && <ProspectProfiler onGenerated={handleProspectGenerated} />}
        </div>
      </div>
      <GuidedTour tourId={TOUR_ID} steps={salesIntelligenceTourSteps} isActive={tour.isActive} onComplete={tour.completeTour} onSkip={tour.skipTour} />
    </div>
  );
};

export default SalesIntelligencePage;
